import {useCallback, useEffect, useRef, useState} from 'react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import Form, {ButtonItem, ButtonOptions, Item, Label, RequiredRule} from 'devextreme-react/form';
import LoadIndicator from 'devextreme-react/load-indicator';
import notify from 'devextreme/ui/notify';
import {useAuth} from '../../../contexts/auth';
import './LoginForm.scss';
import {SignInAuthResult} from "../../../types";
import {EnvironmentData, getEnvironments} from "../../../api/auth";

export default function LoginForm() {
    const navigate = useNavigate();
    const {signIn, externalLogin} = useAuth();
    const [loading, setLoading] = useState(false);
    const [showEnvSelector, setShowEnvSelector] = useState(false);
    const formData = useRef({userName: '', password: '', rememberMe: false, portal: ''});
    const [envs, setEnvs] = useState([] as EnvironmentData[]);
    const companyTypeEditorOptions = {
        stylingMode: 'outlined',
        labelMode: 'floating',
        placeholder: 'Portal',
        dataSource: envs,
        displayExpr: 'name',
        searchEnabled: false,
        valueExpr: 'code',
        showDataBeforeSearchOption: true,
    }
    const locationState = useLocation().state;

    useEffect(() => {
        localStorage.removeItem("RedirectUrl");
        if (locationState?.prevLocation && locationState?.prevLocation !== "/logout") {
            localStorage.setItem("RedirectUrl", locationState.prevLocation);
        }
    }, [])

    function processAuthResult(result: SignInAuthResult) {
        if (result.changePasswordToken) {
            if (result.isPasswordExpired) {
                navigate('/expired-password/' + result.changePasswordToken);
            } else {
                navigate('/first-login-password/' + result.changePasswordToken);
            }

            return;
        }

        localStorage.setItem('access_token', result.token as string);
        if (result.isAdmin) {
            navigate('/company-selector');
            return;
        }

        externalLogin(result.token as string);
        let redirectUrl = localStorage.getItem("RedirectUrl");
        if (redirectUrl) {
            localStorage.removeItem("RedirectUrl");
            navigate(redirectUrl);
        }
    }

    const onSubmit = useCallback(async (e: any) => {
        e.preventDefault();
        const {userName, password, rememberMe} = formData.current;
        let {portal} = formData.current;
        if (portal === '') {
            setLoading(true);
            const envs = await getEnvironments(userName);
            setLoading(false);
            if (envs.length === 1) {
                portal = envs[0].code;
            } else {
                setEnvs(envs);
                setShowEnvSelector(true);
                return;
            }
        }

        const result = await signIn(portal, userName, password);
        if (!result.isOk) {
            setLoading(false);
            notify(result.message, 'error', 2000);
            return;
        }

        processAuthResult(result);
    }, [signIn]);

    return (
        <form className={'login-form'} onSubmit={onSubmit}>
            <Form formData={formData.current} disabled={loading} requiredMark={""}>
                <Item
                    dataField={'userName'}
                    editorType={'dxTextBox'}
                    editorOptions={userNameEditorOptions}
                    visible={!showEnvSelector}
                >
                    <RequiredRule message="User Name is required"/>
                    <Label visible={false}/>
                </Item>
                <Item
                    dataField={'password'}
                    editorType={'dxTextBox'}
                    editorOptions={passwordEditorOptions}
                    visible={!showEnvSelector}
                >
                    <RequiredRule message="Password is required"/>
                    <Label visible={false}/>
                </Item>
                <Item
                    dataField={'rememberMe'}
                    editorType={'dxCheckBox'}
                    editorOptions={rememberMeEditorOptions}
                    visible={!showEnvSelector}
                >
                    <Label visible={false}/>
                </Item>
                <Item
                    dataField={'portal'}
                    editorType={'dxSelectBox'}
                    editorOptions={companyTypeEditorOptions}
                    visible={showEnvSelector}
                >
                    <Label visible={false} text={'Portal'}/>
                </Item>
                <ButtonItem>
                    <ButtonOptions
                        width={'100%'}
                        type={'default'}
                        useSubmitBehavior={true}
                        elementAttr={{testid: 'submit', 'data-testid': 'submit', class: 'login-btn'}}
                    >
                        <span className="dx-button-text">
                            {
                                loading
                                    ? <LoadIndicator width={'24px'} height={'24px'} visible={true}/>
                                    : 'Login'
                            }
                        </span>
                    </ButtonOptions>
                </ButtonItem>
                <Item>
                    <div className={'link'}>
                        <Link to={'/forgot-password'}>Forgot password?</Link>
                    </div>
                </Item>
            </Form>
        </form>
    );
}

const userNameEditorOptions = {
    stylingMode: 'outlined',
    labelMode: 'floating',
    placeholder: 'User Name',
    mode: 'text',
    inputAttr: {testid: 'userName', 'data-testid': 'userName'}
};
const passwordEditorOptions = {
    stylingMode: 'outlined',
    labelMode: 'floating',
    placeholder: 'Password',
    mode: 'password',
    inputAttr: {testid: 'password', 'data-testid': 'password'}
};
const rememberMeEditorOptions = {
    text: 'Remember me',
    elementAttr: {class: 'form-text', testid: 'rememberMe', 'data-testid': 'rememberMe'}
};
